import { motion } from "framer-motion";
import { Boxes, Check, Crown, FileCheck2, Fuel, PlaneTakeoff, Radio } from "lucide-react";
import { SERVICES } from "@/data";
import { useLang } from "@/i18n";
import { AeroImg } from "@/components/AeroImg";

const ICONS = { PlaneTakeoff, FileCheck2, Fuel, Radio, Crown, Boxes };
const EASE = [0.16, 1, 0.3, 1];

function ServiceCard({ s, i }) {
  const { lang } = useLang();
  const Icon = ICONS[s.icon] || PlaneTakeoff;
  const bullets = s[`bullets_${lang}`];

  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="rounded-xl bg-blue-600/15 border border-blue-500/25 p-3 text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
          <Icon size={22} />
        </div>
        <span className="font-mono-tag text-[10px] tracking-widest uppercase text-blue-400/80 border border-blue-500/25 rounded-full px-2.5 py-1">{s.badge}</span>
      </div>
      <h3 className="mt-5 text-xl font-bold text-white">{s[`title_${lang}`]}</h3>
      <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{s[`desc_${lang}`]}</p>
      <ul className="mt-4 space-y-2">
        {bullets.slice(0, 3).map((b, j) => (
          <li key={j} className="flex items-start gap-2 text-[13px] text-slate-300">
            <Check size={14} className="mt-0.5 shrink-0 text-blue-500" />
            {b}
          </li>
        ))}
      </ul>
    </>
  );

  return (
    <motion.article
      data-testid={`service-card-${s.id}`}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.75, delay: (i % 3) * 0.12, ease: EASE }}
      whileHover={{ y: -6 }}
      className={`group relative overflow-hidden rounded-2xl border border-blue-500/15 bg-[#0B1526] ${s.span} ${s.image ? "min-h-[380px] flex flex-col justify-end" : "p-6 lg:p-7"}`}
      {...(s.image ? { "data-always-dark": true } : {})}
    >
      {s.image ? (
        <>
          <div className="absolute inset-0">
            <AeroImg img={s.image} alt={s.title_en} className="h-full w-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1526] via-[#0B1526]/80 to-[#0B1526]/20" />
          <div className="relative p-6 lg:p-8">{inner}</div>
        </>
      ) : (
        inner
      )}
      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-blue-500/0 group-hover:ring-blue-500/40 transition-all duration-500" />
    </motion.article>
  );
}

export function Services() {
  const { t } = useLang();
  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14 max-w-3xl"
        >
          <span className="font-mono-tag text-xs tracking-[0.3em] uppercase text-blue-400" data-testid="services-eyebrow">{t("services_eyebrow")}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white" data-testid="services-title">{t("services_title")}</h2>
          <p className="mt-4 text-base sm:text-lg text-slate-400">{t("services_sub")}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-5" data-testid="services-bento-grid">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
